import type { Query } from '@tanstack/react-query';

/**
 * Tenant-scoped replacement for `keepPreviousData`.
 *
 * `keepPreviousData` reuses the previous key's data whenever ANY key arg
 * changes — including orgId — which would paint the previous tenant's rows on
 * first paint after an org switch. This variant keeps the previous period's
 * data only while the tenant is unchanged, so `isPlaceholderData` can only
 * ever mean "previous period, same tenant" (the state C26 keeps on screen).
 *
 * Reads orgId from key index 2, the slot every capacity/calendar key factory
 * puts it in — pinned by useCapacityQueryKeys.test.ts.
 *
 * `previousQuery` is typed as `Pick<Query, 'queryKey'>` rather than `Query`:
 * `Query.options.persister` makes TQueryFnData invariant, so a bare `Query`
 * parameter is not assignable to TanStack's `placeholderData` function form.
 * The key is all this helper reads, so narrowing the parameter to it keeps the
 * function assignable at every instantiation.
 */
export function keepPreviousDataForOrg<T>(orgId: string | null | undefined) {
  return (
    previousData: T | undefined,
    previousQuery: Pick<Query, 'queryKey'> | undefined,
  ): T | undefined => {
    if (previousData === undefined || !previousQuery) return undefined;
    return previousQuery.queryKey[2] === orgId ? previousData : undefined;
  };
}
